import React, { useCallback, useEffect, useState } from "react";
import { apptApi, invoiceApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Stat, Bars, Section, Loading } from "../../Components/UI/ui";
import { GATE, STATUS_ORDER, STATUS_LABEL, INVOICE_STATUSES, money, todayStr, addDays } from "../../lib/lab";

const PRESETS = [
  { id: "today", label: "Today", from: () => todayStr() },
  { id: "7", label: "7 days", from: () => addDays(todayStr(), -6) },
  { id: "30", label: "30 days", from: () => addDays(todayStr(), -29) },
  { id: "90", label: "90 days", from: () => addDays(todayStr(), -89) },
];

// Appointments summary for everyone; billing summary for admin/owner.
const Analytic = () => {
  const { can } = useAuth();
  const { toast } = useApp();
  const admin = can(...GATE.billingAdmin);
  const [preset, setPreset] = useState("7");
  const [startDate, setStartDate] = useState(PRESETS[1].from());
  const [endDate, setEndDate] = useState(todayStr());
  const [appt, setAppt] = useState(null);
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    setPreset(id);
    setStartDate(p.from());
    setEndDate(todayStr());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const range = { startDate, endDate };
      const [a, b] = await Promise.all([apptApi.summary(range), admin ? invoiceApi.summary(range) : Promise.resolve(null)]);
      setAppt(a); setBill(b);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [startDate, endDate, admin, toast]);

  useEffect(() => { load(); }, [load]);

  const by = appt?.byStatus || {};
  const done = by.COMPLETED ?? 0;
  const lost = (by.CANCELLED ?? 0) + (by.NO_SHOW ?? 0);

  return (
    <Page
      title="Analytics"
      actions={
        <>
          {PRESETS.map((p) => <Button key={p.id} size="sm" variant={preset === p.id ? "navy" : "secondary"} onClick={() => applyPreset(p.id)}>{p.label}</Button>)}
          <input type="date" className="lab-input" value={startDate} onChange={(e) => { setPreset(null); setStartDate(e.target.value); }} />
          <span className="lab-muted">to</span>
          <input type="date" className="lab-input" value={endDate} onChange={(e) => { setPreset(null); setEndDate(e.target.value); }} />
        </>
      }
    >
      <div className="lab-page__body">
        {loading && !appt ? <Loading /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="lab-stats">
              <Stat accent label="Bookings" value={appt?.total ?? 0} />
              <Stat label="Completed" value={done} sub={appt?.total ? `${Math.round((done / appt.total) * 100)}% of bookings` : ""} />
              <Stat label="Report ready" value={by.REPORT_READY ?? 0} sub="awaiting hand-over" />
              <Stat label="Pending home collections" value={appt?.pendingHomeCollections ?? 0} />
              <Stat label="Cancelled / no-show" value={lost} />
              {admin && bill && (
                <>
                  <Stat accent label="Collected" value={money(bill.collected)} sub={`of ${money(bill.invoiced)} invoiced`} />
                  <Stat label="Outstanding" value={money(bill.outstanding)} sub={`${bill.overdue?.count ?? 0} overdue · ${money(bill.overdue?.amount)}`} />
                </>
              )}
            </div>

            <div className={`lab-grid ${admin ? "cols-3" : "cols-1"}`}>
              <Section title="Bookings by stage">
                <Bars items={STATUS_ORDER.map((s) => ({ label: STATUS_LABEL[s], value: by[s] ?? 0 }))} />
              </Section>
              {admin && bill && (
                <>
                  <Section title="Revenue by invoice status">
                    <Bars color="y" items={INVOICE_STATUSES.map((s) => ({ label: `${s} (${bill.byStatus?.[s]?.count ?? 0})`, value: bill.byStatus?.[s]?.amount ?? 0 }))} format={money} />
                  </Section>
                  <Section title="Top services by revenue">
                    {(bill.topServices || []).length === 0 ? <span className="lab-muted">No paid services in range.</span> :
                      <Bars items={bill.topServices.map((t) => ({ label: `${t.name} ×${t.quantity}`, value: t.revenue }))} format={money} />}
                  </Section>
                  <Section title="Payment methods">
                    {(bill.paymentSplit || []).length === 0 ? <span className="lab-muted">No payments in range.</span> :
                      <Bars color="y" items={bill.paymentSplit.map((p) => ({ label: `${p.method} (${p.count})`, value: p.amount }))} format={money} />}
                  </Section>
                </>
              )}
            </div>
            {!admin && <span className="lab-muted">Revenue figures are visible to lab admins and owners.</span>}
          </div>
        )}
      </div>
    </Page>
  );
};

export default Analytic;
